import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, FileText, Users, Grid3x3 as Grid3X3, ChevronDown } from 'lucide-react';
import { resolveModuleAccess, type ModuleAccess } from '../lib/moduleAccess';
import { useLanguage } from '../contexts/LanguageContext';

interface HubSwitcherProps {
  /** Hub the switcher is rendered in — excluded from the menu. */
  current: 'main' | 'content' | 'creators';
  className?: string;
}

export function HubSwitcher({ current, className = '' }: HubSwitcherProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [access, setAccess] = useState<ModuleAccess | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    resolveModuleAccess().then(setAccess).catch(() => setAccess(null));
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!access) return null;

  const items: { key: string; label: string; icon: typeof LayoutGrid; path: string }[] = [];
  if (access.mainApp && current !== 'main') {
    items.push({ key: 'main', label: t('hubSwitcher.mainApp'), icon: LayoutGrid, path: '/main' });
  }
  if (access.contentHub && current !== 'content') {
    items.push({ key: 'content', label: 'Content Hub', icon: FileText, path: '/contenthub' });
  }
  if ((access.creatorsHubOwn || access.isStaff) && current !== 'creators') {
    items.push({
      key: 'creators',
      label: 'Creators Hub',
      icon: Users,
      // Own account goes straight to the portal; staff pick a creator first.
      path: access.creatorsHubOwn ? '/creatorshub/portal' : '/apps?creators=1',
    });
  }

  if (items.length === 0) return null;

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="pressable flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-full shadow-sm ring-1 ring-gray-100 hover:ring-gray-200 hover:shadow transition-all text-sm font-medium"
        title={t('hubSwitcher.title')}
      >
        <Grid3X3 className="w-4 h-4" />
        <span className="hidden lg:inline">{t('hubSwitcher.title')}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white/95 backdrop-blur-md rounded-2xl shadow-float ring-1 ring-gray-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200 origin-top-right py-1.5">
          {items.map(item => (
            <button
              key={item.key}
              onClick={() => { setOpen(false); navigate(item.path); }}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
            >
              <item.icon className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
          <div className="border-t border-gray-100 mt-1.5 pt-1.5">
            <button
              onClick={() => { setOpen(false); navigate('/apps'); }}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-3 transition-colors"
            >
              <Grid3X3 className="w-4 h-4 text-gray-400" />
              <span>{t('hubSwitcher.allModules')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
